import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MusicPlayerComponent } from '../../../types/builder';
import { ColorPicker } from '../../Common/ColorPicker';
import { Upload, Loader } from 'lucide-react';
import toast from 'react-hot-toast';

interface MusicPlayerSettingsProps {
  component: MusicPlayerComponent;
  onUpdate: (updates: Partial<MusicPlayerComponent['settings']>) => void;
}

const STYLE_OPTIONS = [
  { value: 'default', key: 'default' },
  { value: 'minimal', key: 'minimal' },
  { value: 'compact', key: 'compact' },
  { value: 'modern', key: 'modern' },
] as const;

export const MusicPlayerSettings: React.FC<MusicPlayerSettingsProps> = ({ component, onUpdate }) => {
  const { t } = useTranslation('builder');
  const { settings } = component;
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);

  const handleAudioUpload = async (file: File) => {
    if (!file.type.startsWith('audio/')) {
      toast.error(t('settings.musicPlayer.audioOnly'));
      return;
    }

    if (file.size > 20 * 1024 * 1024) {
      toast.error(t('settings.musicPlayer.audioTooLarge'));
      return;
    }

    setUploadingAudio(true);

    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        toast.error(t('settings.musicPlayer.loginToUploadAudio'));
        return;
      }

      const formData = new FormData();
      formData.append('audio', file);

      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
      const response = await fetch(`${API_URL}/upload/audio`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || t('settings.musicPlayer.audioUploadFailed'));
      }

      const data = await response.json();
      onUpdate({ audioUrl: data.url });
      toast.success(t('settings.musicPlayer.audioUploadSuccess'));
    } catch (error: any) {
      console.error('Audio upload error:', error);
      toast.error(error.message || t('settings.musicPlayer.audioUploadFailed'));
    } finally {
      setUploadingAudio(false);
    }
  };

  const handleCoverUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error(t('settings.musicPlayer.imageOnly'));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('settings.musicPlayer.imageTooLarge5MB'));
      return;
    }

    setUploadingCover(true);

    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        toast.error(t('settings.musicPlayer.loginToUploadImage'));
        return;
      }

      const formData = new FormData();
      formData.append('image', file);

      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
      const response = await fetch(`${API_URL}/upload/image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || t('settings.musicPlayer.coverUploadFailed'));
      }

      const data = await response.json();
      onUpdate({ coverImage: data.url });
      toast.success(t('settings.musicPlayer.coverUploadSuccess'));
    } catch (error: any) {
      console.error('Cover upload error:', error);
      toast.error(error.message || t('settings.musicPlayer.coverUploadFailed'));
    } finally {
      setUploadingCover(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-sm font-semibold text-white mb-3">{t('settings.musicPlayer.heading')}</h3>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">{t('settings.common.title')}</label>
        <input
          type="text"
          value={settings.title}
          onChange={(e) => onUpdate({ title: e.target.value })}
          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder={t('settings.musicPlayer.titlePlaceholder')}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          {t('settings.musicPlayer.audioUrl')}
        </label>
        <input
          type="text"
          value={settings.audioUrl}
          onChange={(e) => onUpdate({ audioUrl: e.target.value })}
          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder={t('settings.musicPlayer.audioUrlPlaceholder')}
        />

        <label className="flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded cursor-pointer transition-colors text-sm mt-2">
          <input
            type="file"
            accept="audio/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleAudioUpload(file);
            }}
            disabled={uploadingAudio}
          />
          {uploadingAudio ? (
            <>
              <Loader className="w-4 h-4 animate-spin" />
              <span>{t('settings.common.uploading')}</span>
            </>
          ) : (
            <>
              <Upload size={16} />
              <span>{t('settings.musicPlayer.uploadAudio')}</span>
            </>
          )}
        </label>

        <p className="text-xs text-gray-500 mt-1">{t('settings.musicPlayer.audioFormatHint')}</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          {t('settings.musicPlayer.songName')}
        </label>
        <input
          type="text"
          value={settings.songName}
          onChange={(e) => onUpdate({ songName: e.target.value })}
          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder={t('settings.musicPlayer.songNamePlaceholder')}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          {t('settings.musicPlayer.artistName')}
        </label>
        <input
          type="text"
          value={settings.artistName}
          onChange={(e) => onUpdate({ artistName: e.target.value })}
          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder={t('settings.musicPlayer.artistNamePlaceholder')}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          {t('settings.musicPlayer.coverImageUrl')}
        </label>
        <input
          type="text"
          value={settings.coverImage || ''}
          onChange={(e) => onUpdate({ coverImage: e.target.value })}
          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder={t('settings.musicPlayer.coverImageUrlPlaceholder')}
        />

        <label className="flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded cursor-pointer transition-colors text-sm mt-2">
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleCoverUpload(file);
            }}
            disabled={uploadingCover}
          />
          {uploadingCover ? (
            <>
              <Loader className="w-4 h-4 animate-spin" />
              <span>{t('settings.common.uploading')}</span>
            </>
          ) : (
            <>
              <Upload size={16} />
              <span>{t('settings.musicPlayer.uploadCover')}</span>
            </>
          )}
        </label>

        <p className="text-xs text-gray-400 mt-1">{t('settings.musicPlayer.coverImageHint')}</p>

        {settings.coverImage && (
          <div className="mt-2">
            <img
              src={settings.coverImage}
              alt={t('settings.musicPlayer.coverAlt')}
              className="w-32 h-32 object-cover rounded-lg"
            />
          </div>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          {t('settings.musicPlayer.playerStyle')}
        </label>
        <select
          value={settings.style || 'default'}
          onChange={(e) => onUpdate({ style: e.target.value as 'default' | 'minimal' | 'compact' | 'modern' })}
          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {STYLE_OPTIONS.map((s) => (
            <option key={s.value} value={s.value}>
              {t(`settings.musicPlayer.styles.${s.key}` as const)}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="flex items-center gap-2 cursor-pointer mb-3">
          <input
            type="checkbox"
            checked={settings.autoPlay}
            onChange={(e) => onUpdate({ autoPlay: e.target.checked })}
            className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-300">{t('settings.musicPlayer.autoPlayOnLoad')}</span>
        </label>
        <p className="text-xs text-gray-500 ml-6 mb-3">{t('settings.musicPlayer.autoPlayHint')}</p>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={settings.loop}
            onChange={(e) => onUpdate({ loop: e.target.checked })}
            className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-300">{t('settings.musicPlayer.loopPlayback')}</span>
        </label>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">{t('settings.common.backgroundColor')}</label>
        <ColorPicker
          color={settings.backgroundColor}
          onChange={(color) => onUpdate({ backgroundColor: color })}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">{t('settings.common.textColor')}</label>
        <ColorPicker
          color={settings.textColor}
          onChange={(color) => onUpdate({ textColor: color })}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          {t('settings.musicPlayer.accentColorControls')}
        </label>
        <ColorPicker
          color={settings.accentColor}
          onChange={(color) => onUpdate({ accentColor: color })}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          {t('settings.common.paddingPx', { value: settings.padding })}
        </label>
        <input
          type="range"
          min="0"
          max="64"
          value={settings.padding}
          onChange={(e) => onUpdate({ padding: parseInt(e.target.value) })}
          className="w-full"
        />
      </div>
    </div>
  );
};
